import { type ReactNode } from 'react';

export type DonutSlice = {
  id: string;
  label: string;
  value: number;
  percent: number;
  color: string;
};

const SIZE = 132;
const RADIUS = 52;
const THICKNESS = 14;
// Slices are separated by a gap in the surface color, never by a stroke
// around each slice: a border would add ink that is not data.
const GAP = 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CENTER = SIZE / 2;

type Arc = { slice: DonutSlice; dash: number; offset: number };

function arcs(slices: DonutSlice[]): Arc[] {
  const total = slices.reduce((sum, s) => sum + s.value, 0) || 1;
  let used = 0;
  return slices.map((slice) => {
    const length = (slice.value / total) * CIRCUMFERENCE;
    const offset = used;
    used += length;
    // A sliver narrower than the gap still gets a visible sliver.
    return { slice, dash: Math.max(length - GAP, 1), offset };
  });
}

type Props = {
  slices: DonutSlice[];
  activeId: string | null;
  onHover: (id: string | null) => void;
};

type ArcProps = {
  arc: Arc;
  dimmed: boolean;
  onHover: (id: string | null) => void;
};

function ArcPath({ arc, dimmed, onHover }: ArcProps): ReactNode {
  const { slice, dash, offset } = arc;
  return (
    <circle
      cx={CENTER}
      cy={CENTER}
      r={RADIUS}
      fill="none"
      stroke={slice.color}
      strokeWidth={THICKNESS}
      strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
      strokeDashoffset={-offset}
      className="transition-opacity duration-200"
      opacity={dimmed ? 0.25 : 1}
      onMouseEnter={() => onHover(slice.id)}
      onMouseLeave={() => onHover(null)}
    >
      <title>{`${slice.label}: ${slice.value} (${slice.percent}%)`}</title>
    </circle>
  );
}

// Keeps the ring readable when every slice is a sliver.
function Track(): ReactNode {
  return (
    <circle
      cx={CENTER}
      cy={CENTER}
      r={RADIUS}
      fill="none"
      stroke="currentColor"
      strokeWidth={THICKNESS}
      className="text-white/[0.04]"
    />
  );
}

export function DonutChart({ slices, activeId, onHover }: Props): ReactNode {
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="h-[132px] w-[132px] shrink-0"
      aria-hidden="true"
    >
      <Track />
      <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
        {arcs(slices).map((arc) => (
          <ArcPath
            key={arc.slice.id}
            arc={arc}
            dimmed={activeId !== null && activeId !== arc.slice.id}
            onHover={onHover}
          />
        ))}
      </g>
    </svg>
  );
}
